import React from "react";
import cn from "classnames";

export type AutocompleteOption = { title: string; value: string | number };

type Props = {
  label: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** The selected value, or "" for nothing selected. */
  value: string | number;
  /** What to show for the current value. Kept by the caller, which owns it. */
  selectedTitle?: string;
  onChange: (value: string, option: AutocompleteOption | null) => void;
  /**
   * Fetch one page of matches. Page is zero based. Returning fewer than
   * pageSize is how this knows there is nothing more to load.
   */
  search: (term: string, page: number) => Promise<AutocompleteOption[]>;
  pageSize?: number;
  /** Shown when a search comes back with nothing. */
  emptyMessage?: string;
  /** Re-runs the open search when this changes - e.g. a different client. */
  scopeKey?: string | number;
};

const DEBOUNCE_MS = 250;

/**
 * A text box that searches as you type and offers what it finds.
 *
 * Every picker in the app was a plain select fed by "load all the rows and hand
 * them to the browser". That works until a clinic has more than a few hundred
 * of something, at which point the page is slow and the list is unusable. This
 * asks the server for a page of matches instead.
 *
 * Typing is debounced so a five letter name is one request, not five, and
 * replies are matched to the request that asked for them - otherwise a slow
 * search for "be" can land after "bella" and overwrite better results with
 * worse ones.
 */
const Autocomplete = ({
  label,
  placeholder,
  className,
  disabled,
  value,
  selectedTitle,
  onChange,
  search,
  pageSize = 20,
  emptyMessage = "No matches",
  scopeKey,
}: Props) => {
  const id = React.useId();

  const [open, setOpen] = React.useState(false);
  const [term, setTerm] = React.useState("");
  const [options, setOptions] = React.useState<AutocompleteOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [highlighted, setHighlighted] = React.useState(0);

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  // Only the newest request is allowed to write results.
  const requestRef = React.useRef(0);

  const runSearch = React.useCallback(
    async (nextTerm: string, nextPage: number, append: boolean) => {
      const ticket = ++requestRef.current;
      setLoading(true);
      try {
        const found = await search(nextTerm, nextPage);
        if (ticket !== requestRef.current) return;
        setOptions((prev) => (append ? [...prev, ...found] : found));
        setHasMore(found.length >= pageSize);
        if (!append) setHighlighted(0);
      } catch {
        if (ticket !== requestRef.current) return;
        // An empty list reads as "nothing matched", which is the least
        // confusing thing to show when a lookup fails mid-type.
        if (!append) setOptions([]);
        setHasMore(false);
      } finally {
        if (ticket === requestRef.current) setLoading(false);
      }
    },
    [search, pageSize]
  );

  // Debounced search while the list is open.
  React.useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setPage(0);
      runSearch(term, 0, false);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term, open, scopeKey, runSearch]);

  // A change of scope invalidates both the results and the selection made from
  // them - a patient picked for one client does not belong to the next.
  React.useEffect(() => {
    setOptions([]);
    setTerm("");
  }, [scopeKey]);

  React.useEffect(() => {
    if (!open) return;
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, [open]);

  const select = (option: AutocompleteOption) => {
    onChange(String(option.value), option);
    setOpen(false);
    setTerm("");
  };

  const clear = () => {
    onChange("", null);
    setTerm("");
    setOptions([]);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const delta = event.key === "ArrowDown" ? 1 : -1;
      setHighlighted((current) => {
        if (options.length === 0) return 0;
        return (current + delta + options.length) % options.length;
      });
      return;
    }
    if (event.key === "Enter" && open && options[highlighted]) {
      event.preventDefault();
      select(options[highlighted]);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  // Closed, the box shows what is selected. Open, it shows what is being typed,
  // so the selection is still readable at a glance without blocking a search.
  const displayValue = open ? term : selectedTitle ?? "";

  return (
    <div className={cn("flex flex-col gap-y-1.5", className && className)} ref={rootRef}>
      <label htmlFor={id} className="text-slate-600 text-sm font-medium">
        {label}
      </label>

      <div className="relative">
        <input
          id={id}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}__list`}
          disabled={disabled}
          value={displayValue}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className="w-full rounded-md border border-slate-300 bg-white text-sm px-3 py-2 pr-8 min-w-0 focus:outline-none focus:border-vg-blue focus:ring-[3px] focus:ring-vg-blue/15 transition-colors disabled:bg-slate-50 disabled:text-slate-400"
        />

        {value !== "" && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label={`Clear ${label}`}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg leading-none px-1"
          >
            &times;
          </button>
        )}

        {open && (
          <ul
            id={`${id}__list`}
            role="listbox"
            className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg py-1"
          >
            {options.map((option, index) => (
              <li
                key={String(option.value)}
                role="option"
                aria-selected={String(option.value) === String(value)}
                onMouseEnter={() => setHighlighted(index)}
                onMouseDown={(e) => {
                  // mousedown, not click: the input's blur would close the list
                  // before a click ever landed.
                  e.preventDefault();
                  select(option);
                }}
                className={cn(
                  "px-3 py-2 text-sm cursor-pointer",
                  index === highlighted ? "bg-vg-blue/10 text-slate-900" : "text-slate-700"
                )}
              >
                {option.title}
              </li>
            ))}

            {!loading && options.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">{emptyMessage}</li>
            )}

            {loading && (
              <li className="px-3 py-2 text-sm text-slate-500">Searching&hellip;</li>
            )}

            {hasMore && !loading && (
              <li>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const next = page + 1;
                    setPage(next);
                    runSearch(term, next, true);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-vg-blue hover:bg-slate-50"
                >
                  Show more
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Autocomplete;
